/* eslint-disable consistent-return */
import _get from 'lodash/get';
import { formatCurrency, trimString } from '@atc/string-fns';
const MAX_NUM_ITEMS = 3; // number of top priority criteria to display in subheading
const filterPriority = ['yearRange', 'makeCodeList', 'modelCodeList', 'priceRange', 'listingTypes', 'vehicleStyleCodes', 'transmissionCodes', 'trimCodeList', 'driveGroup', 'engineCodes', 'extColorsSimple'];

/**
 * Build fullHeading and heading using SRP title
 * If the title is only "Cars for Sale...", the headings will be "Multiple Car Search"
 * Otherwise, we'll remove "for Sale" from the title
 */
export function getHeadings(_ref) {
  let {
    locationHeading = ''
  } = _ref;
  if (locationHeading.indexOf('Cars') === 0) {
    return {
      fullHeading: 'Multiple Car Search',
      heading: 'Multiple Car Search'
    };
  }
  const index = locationHeading.indexOf(' for Sale');
  const fullHeading = decodeURIComponent(index > -1 ? locationHeading.slice(0, index) : locationHeading);
  return {
    fullHeading,
    heading: trimString(fullHeading, 30)
  };
}

/**
 * Build yearRange description: startYear - endYear
 * Ignore the case of:
 * - startYear === endYear since SRP title will have the year already
 * - startYear and endYear are default values
 */
export function getYearRangeValues(_ref2) {
  let {
    requestValues = {},
    options = {}
  } = _ref2;
  const {
    startYear = '',
    endYear = ''
  } = requestValues;
  const isRange = startYear !== endYear;
  const optionKeys = Object.keys(options);
  if (isRange && optionKeys.length > 0) {
    const isDefaultYearRange = optionKeys[0] === startYear && optionKeys[optionKeys.length - 1] === endYear;
    if (!isDefaultYearRange) {
      return `${startYear} - ${endYear}`;
    }
  }
  return null;
}

/**
 * Build priceRange description:
 * If both minPrice and maxPrice are not empty: {minPrice} - {maxPrice}
 * If maxPrice is empty: over {minPrice}
 * If minPrice is empty: under {maxPrice}
 */
export function getPriceRangeValues(_ref3) {
  let {
    requestValues = {}
  } = _ref3;
  const {
    minPrice,
    maxPrice
  } = requestValues;
  if (minPrice || maxPrice) {
    const minPriceLabel = minPrice && formatCurrency(minPrice);
    const maxPriceLabel = maxPrice && formatCurrency(maxPrice);
    if (minPrice && maxPrice) {
      return minPriceLabel + ' - ' + maxPriceLabel;
    }
    return minPriceLabel ? 'over ' + minPriceLabel : 'under ' + maxPriceLabel;
  }
  return null;
}

/**
 * Determine if Make/Model/Trim description needs to be included in subheading
 * When 1 make OR 1 make + 1 model is selected, the SRP title contains them so we don't need them in subheading
 * Return true when:
 * * multiple makes
 * * multiple makes + 1 model
 * * multiple makes + multiple models
 */
export function shouldIncludeMMT(_ref4) {
  let {
    param = '',
    isMultipleSelected = false,
    mmtIndex = -1
  } = _ref4;
  switch (param) {
    case 'makeCodeList':
      {
        return isMultipleSelected;
      }
    case 'modelCodeList':
      {
        return isMultipleSelected || mmtIndex > -1;
      }
    default:
      return true;
  }
}
export function getMMTDescription(_ref5) {
  let {
    parentLabel = null,
    label = ''
  } = _ref5;
  const space = label.length > 0 && parentLabel ? ' ' : '';
  return `${parentLabel || ''}${space}${label}`;
}

/**
 * build Make Model Trim description (Make Model1 & Model2, Make Model1 Trim1|Trim2 & Model2)
 * e.g. BMW 228i & 230i, Acura
 */
export function getMMTValues(_ref6) {
  let {
    mmtFilters = [],
    labelsByValues = {},
    requestValues = {}
  } = _ref6;
  const hasMake = mmtFilters.includes('makeCodeList');
  const hasModel = mmtFilters.includes('modelCodeList');

  // make sure values are iterable
  const makeValues = [].concat(_get(requestValues, 'makeCodeList', []));
  const modelValues = [].concat(_get(requestValues, 'modelCodeList', []));
  const trimValues = [].concat(_get(requestValues, 'trimCodeList', []));
  const results = makeValues.map(makeCode => {
    const makeLabel = _get(labelsByValues, ['makeCodeList', makeCode]);

    // build model description for each make
    const modelTrimLabel = modelValues.reduce((acc, modelCode) => {
      const modelLabel = _get(labelsByValues, ['modelCodeList', `modelCodeList|${makeCode}`, modelCode]);
      if (modelLabel) {
        // build trim description for each model
        acc.push(getMMTDescription({
          parentLabel: hasModel && modelLabel,
          label: trimValues.reduce((tacc, trimCode) => {
            const trimLabel = _get(labelsByValues, ['trimCodeList', `trimCodeList|${modelCode}`, trimCode]);
            if (trimLabel) {
              tacc.push(trimLabel);
            }
            return tacc;
          }, []).join('|')
        }));
      }
      return acc;
    }, []);
    return getMMTDescription({
      parentLabel: hasMake && makeLabel,
      label: modelTrimLabel.join(' & ')
    });
  });
  return results.join(', ');
}
export function getSubHeadings(_ref7) {
  let {
    requestValues = {},
    labelsByValues = {}
  } = _ref7;
  const mmtFilters = []; // keep included make/model/trim filters and build subheading at the end
  let mmtIndex = -1; // index of mmt in reducedSearchCriteria

  const reducedCriteria = []; // array to keep string description for each criteria

  filterPriority.some(param => {
    if (reducedCriteria.length + mmtFilters.length >= MAX_NUM_ITEMS) {
      return true;
    }
    let addedCriteria = null;
    if (param === 'yearRange') {
      addedCriteria = getYearRangeValues({
        requestValues,
        options: labelsByValues.yearRange
      });
    } else if (param === 'priceRange') {
      addedCriteria = getPriceRangeValues({
        requestValues
      });
    } else if (requestValues[param]) {
      const selectedValues = _get(requestValues, [param], []);
      const isMultipleSelected = Array.isArray(selectedValues) && selectedValues.length > 1;
      if (['makeCodeList', 'modelCodeList', 'trimCodeList'].includes(param)) {
        // we will be handling MMT later
        if (shouldIncludeMMT({
          param,
          isMultipleSelected,
          mmtIndex
        })) {
          mmtFilters.push(param);
          // reserve index for reducedCriteria
          mmtIndex = mmtIndex > -1 ? mmtIndex : reducedCriteria.length;
        }
      } else {
        const labels = _get(labelsByValues, [param], {});
        // drop if listingTypes or vehicleStyleCodes has only one value selected since it will be in the heading anyways
        // other filters should be included
        if (!(['listingTypes', 'vehicleStyleCodes'].includes(param) && !isMultipleSelected)) {
          const valuesInArray = [].concat(selectedValues);
          addedCriteria = valuesInArray.map(value => labels[value]).join(', ');
        }
      }
    }
    if (addedCriteria) {
      reducedCriteria.push(addedCriteria);
    }
  });
  if (mmtFilters.length > 0) {
    // build make/model/trim description
    // and put it in the reserved spot in reducedCriteria array so we don't have to re-order
    reducedCriteria.splice(mmtIndex, 0, getMMTValues({
      mmtFilters,
      labelsByValues,
      requestValues
    }));
  }
  const fullSubHeading = reducedCriteria.join(', ');
  return {
    fullSubHeading,
    subHeading: trimString(fullSubHeading, 55)
  };
}
export function isSearch(_ref8) {
  let {
    requestValues = {},
    headings = {},
    subHeadings = {}
  } = _ref8;
  if (headings.heading === 'Multiple Car Search' && subHeadings.subHeading === '') {
    // Prevent storing searches that yield unhelpful search descriptions.
    return false;
  }

  // Ignoring the location and year criteria, if `criteria` only specifies `vehicleStyleCodes` and `vehicleStyleCodes`
  // only contains one style then prevent this search from being stored.
  const hasOnlyOneKey = Object.keys(requestValues).filter(key => !['zip', 'searchRadius', 'startYear', 'endYear', 'marketExtension'].includes(key)).length === 1;
  if (hasOnlyOneKey && requestValues.vehicleStyleCodes) {
    const {
      vehicleStyleCodes
    } = requestValues;
    const isArray = Array.isArray(vehicleStyleCodes);
    if (!isArray || isArray && vehicleStyleCodes.length === 1) {
      return false;
    }
  }
  return true;
}